// The return briefing on the seat card of the hub (ui-map-interface.md
// §12.6, R1605, R1606; card 05 of mockup round 076).
//
// The seat beat carries the sums of an absence while nobody sits at the
// seat, and the rows stand under WHILE YOU WERE OUT until the commander
// resumes the seat. The head of the INBOX of card 04 is SINCE YOU LEFT of
// `ui/inbox/sinceleft.ts` now, which sums the inbox ledger (inbox.md §7,
// R1851); the world's `ReturnBriefing` frame still gives it the departure
// and the sieges of the absence.
//
// This module is pure: the rows, their glyphs and their markup. The PLAY
// panel owns the card.

import { briefing as S } from '../gen/strings';
import type { SeatBriefing, SeatSiege } from '../net/api';
import { groupThousands as num } from '../locale/group';
import { factionName } from '../world';
import { esc } from './esc';
import { withMarkIcons } from './resicon';

/** The six rows, in the order of need: a siege against the commander
 *  first, then the war, the captains, the money and the works. */
export type BriefKey = 'siege' | 'cells' | 'fleets' | 'sickbay' | 'auction' | 'works';

/** The glyph of each row: ⚠ a siege, ⬡ territory, ☠ a fleet sunk, ✚ the
 *  sickbay, ¤ the market and ✓ the works. The markup draws ¤ as the drawn
 *  icon of `resicon.ts` (`withMarkIcons`, R1768). */
const BRIEF_GLYPHS: Readonly<Record<BriefKey, string>> = {
  siege: '⚠', cells: '⬡', fleets: '☠', sickbay: '✚', auction: '¤', works: '✓',
};

/** One row, every string decided. `figure` and `detail` are markup,
 *  escaped here. */
export interface BriefRow {
  key: BriefKey;
  glyph: string;
  label: string;
  figure: string;
  detail: string;
  urgent: boolean;
  /** The cell the press centres the map on, 0 for none. */
  cell: number;
}

/** `+won −lost`, each part only when it is not zero. */
function plusMinus(plus: number, minus: number): string {
  const out: string[] = [];
  if (plus > 0) out.push(`<span class="pos">+${num(plus)}</span>`);
  if (minus > 0) out.push(`<span class="neg">−${num(minus)}</span>`);
  return out.join(' ');
}

/** `sunk · lost`, the loss in the danger ink. */
function sunkLost(sunk: number, lost: number): string {
  return `${num(sunk)} <span class="muted">·</span> `
    + (lost > 0 ? `<span class="neg">${num(lost)}</span>` : num(lost));
}

/** A line of names, and `+N MORE` when the row counts more than it names. */
function more(text: string, extra: number): string {
  return extra > 0 ? `${text} · ${S.more({ n: extra })}` : text;
}

/**
 * The siege row of the seat card, from a list of sieges in wall seconds
 * counted from the beat, or null for none: the first siege, and `+N MORE`
 * for the rest. The return briefing and the seat watch (R1743) both carry
 * the list, and both draw it with this row.
 */
export function seatSiegeRow(
  sieges: readonly SeatSiege[], span: (sec: number) => string, ago: number,
): BriefRow | null {
  const g = sieges[0];
  if (g === undefined) return null;
  const left = g.windowInSec - ago;
  const site = esc(g.siteName);
  const attacker = esc(factionName(g.attacker));
  const detail = left < 0
    ? S.siege_open({ site, attacker })
    : S.siege_in({ site, attacker, left: esc(span(left)) });
  return {
    key: 'siege', glyph: BRIEF_GLYPHS.siege, label: S.row_siege,
    figure: left < 0 ? S.window_open : esc(span(left)),
    detail: more(detail, sieges.length - 1), urgent: true, cell: 0,
  };
}

/**
 * The rows of the seat card (card 05), from the seat beat's sums. `span`
 * writes a span of wall seconds and `ago` is how many wall seconds the
 * beat is old, so a countdown counts from now and not from the beat.
 */
export function seatRows(b: SeatBriefing, span: (sec: number) => string, ago: number): BriefRow[] {
  const rows: BriefRow[] = [];
  // The siege row is the one urgent row, and `seatSiegeRow` builds it.
  const push = (key: BriefKey, label: string, figure: string, detail: string) =>
    rows.push({ key, glyph: BRIEF_GLYPHS[key], label, figure, detail, urgent: false, cell: 0 });
  const siege = seatSiegeRow(b.sieges ?? [], span, ago);
  if (siege !== null) rows.push(siege);
  if (b.cellsWon > 0 || b.cellsLost > 0) {
    push('cells', S.row_cells, plusMinus(b.cellsWon, b.cellsLost),
      S.cells({ won: num(b.cellsWon), lost: num(b.cellsLost) }));
  }
  if (b.fleetsSunk > 0 || b.fleetsLost > 0) {
    push('fleets', S.row_fleets, sunkLost(b.fleetsSunk, b.fleetsLost),
      S.fleets({ sunk: num(b.fleetsSunk), lost: num(b.fleetsLost) }));
  }
  const sickbay = b.sickbay ?? [];
  if (sickbay.length > 0) {
    const c = sickbay[0]!;
    push('sickbay', S.row_sickbay, num(sickbay.length),
      more(S.sickbay_in({ name: esc(c.name), left: esc(span(c.backSec - ago)) }), sickbay.length - 1));
  }
  if (b.lotsSold > 0) {
    push('auction', S.row_auction, `<span class="pos">+${num(b.saleCredits)}</span>`,
      S.auction({ lots: num(b.lotsSold), credits: num(b.saleCredits) }));
  }
  if (b.buildLevels > 0 || b.techNodes > 0) {
    push('works', S.row_works, num(b.buildLevels + b.techNodes),
      S.works_hub({ levels: num(b.buildLevels), nodes: num(b.techNodes) }));
  }
  return rows;
}

/** One row as the seat card draws it: no press, the figure on the right. */
export function seatRowHtml(r: BriefRow): string {
  return `<div class="brow${r.urgent ? ' urgent' : ''}"><span class="g">${withMarkIcons(r.glyph)}</span>`
    + `<span class="t"><span class="l">${esc(r.label)}</span><span class="s">${r.detail}</span></span>`
    + `<span class="f">${r.figure}</span></div>`;
}
